<template>
  <div class="availability-combobox" @focusout="handleFocusOut">
    <label class="visually-hidden" :for="inputId">{{ label }}</label>
    <div class="input-group">
      <input
        :id="inputId"
        ref="input"
        :value="modelValue"
        type="text"
        role="combobox"
        aria-autocomplete="list"
        :aria-controls="listId"
        :aria-expanded="isOpen ? 'true' : 'false'"
        :aria-activedescendant="activeDescendant"
        :autocomplete="autocomplete"
        :inputmode="inputmode"
        :placeholder="placeholder"
        :disabled="disabled"
        @focus="open"
        @input="handleInput"
        @keydown="handleKeydown"
      >
      <button
        class="toggle"
        type="button"
        :aria-label="`${label} auswählen`"
        :aria-expanded="isOpen ? 'true' : 'false'"
        :disabled="disabled"
        @mousedown.prevent
        @click="toggle"
      >
        <span aria-hidden="true"></span>
      </button>
    </div>

    <ul v-if="isOpen" :id="listId" class="options" role="listbox" :aria-label="label">
      <li v-if="visibleOptions.length === 0" class="no-results" role="status">
        {{ noResultsLabel }}
      </li>
      <template v-for="(option, index) in visibleOptions" :key="option.value">
        <li v-if="showGroupLabel(option, index)" class="option-group" role="presentation">
          {{ option.group }}
        </li>
        <li role="presentation">
          <button
            :id="optionId(index)"
            class="option"
            :class="{ active: index === activeIndex }"
            type="button"
            role="option"
            :aria-selected="option.label === modelValue ? 'true' : 'false'"
            @mousedown.prevent
            @click="selectOption(option)"
          >
            {{ option.label }}
          </button>
        </li>
      </template>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'AvailabilityCombobox',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    options: {
      type: Array,
      default: () => []
    },
    inputId: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      default: ''
    },
    autocomplete: {
      type: String,
      default: 'off'
    },
    inputmode: {
      type: String,
      default: 'text'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    noResultsLabel: {
      type: String,
      default: 'Keine passenden Einträge gefunden.'
    }
  },
  emits: ['update:modelValue', 'input', 'select', 'submit'],
  data () {
    return {
      isOpen: false,
      activeIndex: -1
    }
  },
  computed: {
    listId () {
      return `${this.inputId}-options`
    },
    visibleOptions () {
      const query = this.normalize(this.modelValue)
      return this.options
        .filter(option => !query || this.normalize(option.label).includes(query))
    },
    activeDescendant () {
      return this.activeIndex >= 0 ? this.optionId(this.activeIndex) : undefined
    }
  },
  watch: {
    visibleOptions () {
      if (this.activeIndex >= this.visibleOptions.length) this.activeIndex = -1
    },
    disabled (value) {
      if (value) this.close()
    }
  },
  mounted () {
    document.addEventListener('pointerdown', this.closeWhenClickingOutside)
  },
  beforeUnmount () {
    document.removeEventListener('pointerdown', this.closeWhenClickingOutside)
  },
  methods: {
    normalize (value) {
      return String(value || '').trim().toLocaleLowerCase('de-DE')
    },
    optionId (index) {
      return `${this.listId}-${index}`
    },
    showGroupLabel (option, index) {
      return Boolean(option.group) && (index === 0 || this.visibleOptions[index - 1]?.group !== option.group)
    },
    open () {
      if (this.disabled) return
      if (!this.isOpen) {
        this.isOpen = true
        this.activeIndex = -1
      }
    },
    close () {
      this.isOpen = false
      this.activeIndex = -1
    },
    toggle () {
      if (this.disabled) return
      if (this.isOpen) {
        this.close()
      } else {
        this.open()
        this.$nextTick(() => this.$refs.input.focus())
      }
    },
    handleInput (event) {
      const value = event.target.value
      this.$emit('update:modelValue', value)
      this.$emit('input', value)
      this.activeIndex = -1
      this.open()
    },
    handleKeydown (event) {
      if (this.disabled) return

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        this.open()
        if (this.visibleOptions.length > 0) {
          this.activeIndex = (this.activeIndex + 1) % this.visibleOptions.length
        }
        return
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        this.open()
        if (this.visibleOptions.length > 0) {
          this.activeIndex = this.activeIndex <= 0 ? this.visibleOptions.length - 1 : this.activeIndex - 1
        }
        return
      }

      if (event.key === 'Enter') {
        event.preventDefault()
        if (this.isOpen && this.activeIndex >= 0) {
          this.selectOption(this.visibleOptions[this.activeIndex])
        } else {
          this.close()
          this.$emit('submit', this.modelValue)
        }
        return
      }

      if (event.key === 'Escape') {
        event.preventDefault()
        this.close()
      }
    },
    selectOption (option) {
      this.$emit('update:modelValue', option.label)
      this.$emit('input', option.label)
      this.$emit('select', option)
      this.close()
    },
    handleFocusOut (event) {
      if (!this.$el.contains(event.relatedTarget)) this.close()
    },
    closeWhenClickingOutside (event) {
      const path = event.composedPath ? event.composedPath() : []
      if (!path.includes(this.$el)) this.close()
    }
  }
}
</script>
